import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Growth (W18): referrals, campus ambassadors + Campus Pass, creator  */
/* codes, restaurant QR posters, first-touch attribution, cashback and */
/* win-back. Discounts always run through the W10 offers engine        */
/* (granted offers); money always lands as W11 Capsico Money with an   */
/* expiry.                                                             */
/* ------------------------------------------------------------------ */

const Id = z.uuid();
const isoInstant = z.iso.datetime({ offset: true });

/** Ops screens poll at this pace. */
export const GROWTH_POLL_SECONDS = 60;
/** Codes people type: letters + digits, upper case. */
export const GrowthCodeSchema = z
  .string()
  .trim()
  .transform((value) => value.toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9]{4,16}$/, 'Codes are 4–16 letters or digits'));
/** Campus Pass ID photos. */
export const CAMPUS_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const CAMPUS_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/** QR poster sizes. */
export const POSTER_SIZES = ['A4', 'A5'] as const;
export const PosterSizeSchema = z.enum(POSTER_SIZES);
export type PosterSize = z.infer<typeof PosterSizeSchema>;

/* ---------------- vocabulary ---------------- */

export const ATTRIBUTION_CHANNELS = [
  'referral',
  'ambassador',
  'creator',
  'qr',
  'campaign',
  'organic',
] as const;
export const AttributionChannelSchema = z.enum(ATTRIBUTION_CHANNELS);
export type AttributionChannel = z.infer<typeof AttributionChannelSchema>;
export const ATTRIBUTION_CHANNEL_LABELS: Record<AttributionChannel, string> = {
  referral: 'Refer a friend',
  ambassador: 'Campus ambassador',
  creator: 'Creator code',
  qr: 'Restaurant QR poster',
  campaign: 'Campaign',
  organic: 'Organic',
};

export const REFERRAL_STATUSES = ['pending', 'rewarded', 'blocked', 'expired'] as const;
export const ReferralStatusSchema = z.enum(REFERRAL_STATUSES);
export type ReferralStatus = z.infer<typeof ReferralStatusSchema>;
export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: 'Waiting for first delivery',
  rewarded: 'Rewarded',
  blocked: 'Not rewarded',
  expired: 'Expired',
};

export const REFERRAL_BLOCKS = [
  'self',
  'same_device',
  'same_address',
  'same_payment',
  'monthly_cap',
  'cancelled',
] as const;
export const ReferralBlockSchema = z.enum(REFERRAL_BLOCKS);
export type ReferralBlock = z.infer<typeof ReferralBlockSchema>;
export const REFERRAL_BLOCK_LABELS: Record<ReferralBlock, string> = {
  self: 'Own code',
  same_device: 'Same phone as the inviter',
  same_address: 'Same delivery address as the inviter',
  same_payment: 'Same payment account as the inviter',
  monthly_cap: 'Inviter reached this month’s limit',
  cancelled: 'First order was cancelled',
};

export const CODE_KINDS = ['referral', 'ambassador', 'creator'] as const;
export const CodeKindSchema = z.enum(CODE_KINDS);
export type CodeKind = z.infer<typeof CodeKindSchema>;

export const CAMPUS_PASS_METHODS = ['email', 'id_photo'] as const;
export const CampusPassMethodSchema = z.enum(CAMPUS_PASS_METHODS);
export type CampusPassMethod = z.infer<typeof CampusPassMethodSchema>;

export const CAMPUS_PASS_STATUSES = [
  'pending_email',
  'pending_photo',
  'pending_review',
  'active',
  'rejected',
  'expired',
] as const;
export const CampusPassStatusSchema = z.enum(CAMPUS_PASS_STATUSES);
export type CampusPassStatus = z.infer<typeof CampusPassStatusSchema>;
export const CAMPUS_PASS_STATUS_LABELS: Record<CampusPassStatus, string> = {
  pending_email: 'Check your college email',
  pending_photo: 'Upload your ID card',
  pending_review: 'Being checked',
  active: 'Active',
  rejected: 'Not approved',
  expired: 'Expired',
};

export const GROWTH_STATUSES = ['active', 'paused'] as const;
export const GrowthStatusSchema = z.enum(GROWTH_STATUSES);
export type GrowthStatus = z.infer<typeof GrowthStatusSchema>;

export const CREATOR_PLATFORMS = ['instagram', 'youtube', 'facebook', 'tiktok', 'other'] as const;
export const CreatorPlatformSchema = z.enum(CREATOR_PLATFORMS);
export type CreatorPlatform = z.infer<typeof CreatorPlatformSchema>;
export const CREATOR_PLATFORM_LABELS: Record<CreatorPlatform, string> = {
  instagram: 'Instagram',
  youtube: 'YouTube',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  other: 'Other',
};

/* ---------------- customer app ---------------- */

export const ReferralHistoryRowSchema = z.object({
  friend: z.string(),
  status: ReferralStatusSchema,
  block: ReferralBlockSchema.nullable(),
  rewardMinor: z.number().int(),
  createdAt: z.string(),
  rewardedAt: z.string().nullable(),
});
export type ReferralHistoryRow = z.infer<typeof ReferralHistoryRowSchema>;

export const MyReferralSchema = z.object({
  code: z.string(),
  /** https link that opens the app with the code applied. */
  shareUrl: z.string(),
  shareText: z.string(),
  country: CountryCodeSchema,
  /** Capsico Money the inviter gets per friend's first delivered order. */
  rewardMinor: z.number().int(),
  rewardDays: z.number().int(),
  /** What the friend gets on their first order (W10 offer). */
  friendOffer: z.object({ code: z.string(), title: z.string() }).nullable(),
  monthCap: z.number().int(),
  rewardsThisMonth: z.number().int(),
  totals: z.object({
    invited: z.number().int(),
    pending: z.number().int(),
    rewarded: z.number().int(),
    earnedMinor: z.number().int(),
  }),
  history: z.array(ReferralHistoryRowSchema),
});
export type MyReferral = z.infer<typeof MyReferralSchema>;

/** Referral, ambassador or creator code typed by (or linked to) a new customer. */
export const ApplyCodeInputSchema = z.object({
  code: GrowthCodeSchema,
});
export type ApplyCodeInput = z.infer<typeof ApplyCodeInputSchema>;

export const ApplyCodeResultSchema = z.object({
  kind: CodeKindSchema,
  channel: AttributionChannelSchema,
  /** The W10 offer now unlocked for the first order (null when the code only attributes). */
  offer: z.object({ code: z.string(), title: z.string() }).nullable(),
  message: z.string(),
});
export type ApplyCodeResult = z.infer<typeof ApplyCodeResultSchema>;

/** After a QR scan or a campaign link opened the app (scanToken from the /q redirect). */
export const AttributionTouchInputSchema = z
  .object({
    scanToken: Id.optional(),
    campaign: z
      .string()
      .trim()
      .regex(/^[a-z0-9_-]{2,40}$/)
      .optional(),
  })
  .refine((value) => value.scanToken !== undefined || value.campaign !== undefined, {
    message: 'Send a scan token or a campaign',
  });
export type AttributionTouchInput = z.infer<typeof AttributionTouchInputSchema>;

export const AttributionTouchResultSchema = z.object({
  attributed: z.boolean(),
  channel: AttributionChannelSchema.nullable(),
  /** Offer code to pre-fill at checkout (QR → WELCOME70). */
  offerCode: z.string().nullable(),
  outletId: Id.nullable(),
});
export type AttributionTouchResult = z.infer<typeof AttributionTouchResultSchema>;

export const CampusSummarySchema = z.object({
  id: Id,
  name: z.string(),
  townName: z.string().nullable(),
  country: CountryCodeSchema,
  freeKm: z.number(),
  emailDomains: z.array(z.string()),
  distanceKm: z.number().nullable(),
});
export type CampusSummary = z.infer<typeof CampusSummarySchema>;

export const CampusNearbyQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
});
export type CampusNearbyQuery = z.infer<typeof CampusNearbyQuerySchema>;

export const MyCampusPassSchema = z.object({
  pass: z
    .object({
      id: Id,
      status: CampusPassStatusSchema,
      method: CampusPassMethodSchema,
      campus: CampusSummarySchema,
      email: z.string().nullable(),
      validFrom: z.string().nullable(),
      validUntil: z.string().nullable(),
      rejectionReason: z.string().nullable(),
      version: z.number().int(),
    })
    .nullable(),
  /** Campuses the customer can apply for (nearest first when a location is known). */
  campuses: z.array(CampusSummarySchema),
  passDays: z.number().int(),
});
export type MyCampusPass = z.infer<typeof MyCampusPassSchema>;

export const CampusPassStartInputSchema = z.object({
  campusId: Id,
  method: CampusPassMethodSchema,
  email: z.email().max(120).optional(),
  ambassadorCode: GrowthCodeSchema.optional(),
});
export type CampusPassStartInput = z.infer<typeof CampusPassStartInputSchema>;

export const CampusEmailVerifyInputSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export type CampusEmailVerifyInput = z.infer<typeof CampusEmailVerifyInputSchema>;

export const CampusPhotoStartInputSchema = z.object({
  contentType: z.enum(CAMPUS_PHOTO_TYPES),
  sizeBytes: z.number().int().min(1).max(CAMPUS_PHOTO_MAX_BYTES),
});
export type CampusPhotoStartInput = z.infer<typeof CampusPhotoStartInputSchema>;

export const CampusPhotoTicketSchema = z.object({
  uploadUrl: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type CampusPhotoTicket = z.infer<typeof CampusPhotoTicketSchema>;

export const CashbackLineSchema = z.object({
  orderId: Id,
  orderNo: z.number().int().nullable(),
  amountMinor: z.number().int(),
  percent: z.number(),
  status: z.enum(['credited', 'skipped']),
  reason: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type CashbackLine = z.infer<typeof CashbackLineSchema>;

export const MyCashbackSchema = z.object({
  country: CountryCodeSchema,
  percent: z.number(),
  minOrderMinor: z.number().int(),
  capMinor: z.number().int(),
  totalMinor: z.number().int(),
  rows: z.array(CashbackLineSchema),
});
export type MyCashback = z.infer<typeof MyCashbackSchema>;

/** Cashback a delivered order earned (shown on the order page). Null when none. */
export const OrderCashbackSchema = z
  .object({ amountMinor: z.number().int(), expiresAt: z.string().nullable() })
  .nullable();
export type OrderCashback = z.infer<typeof OrderCashbackSchema>;

/* ---------------- partner app ---------------- */

export const PartnerQrPosterSchema = z.object({
  id: Id,
  outletId: Id,
  outletName: z.string(),
  slug: z.string(),
  url: z.string(),
  offerCode: z.string(),
  status: GrowthStatusSchema,
  scans7d: z.number().int(),
  scans30d: z.number().int(),
  signups30d: z.number().int(),
  firstOrders30d: z.number().int(),
  createdAt: z.string(),
});
export type PartnerQrPoster = z.infer<typeof PartnerQrPosterSchema>;

export const PartnerQrListSchema = z.object({ rows: z.array(PartnerQrPosterSchema) });
export type PartnerQrList = z.infer<typeof PartnerQrListSchema>;

export const PosterQuerySchema = z.object({ size: PosterSizeSchema.default('A4') });
export type PosterQuery = z.infer<typeof PosterQuerySchema>;

/* ---------------- Capsico OS ---------------- */

export const GrowthSummarySchema = z.object({
  signups7d: z.number().int(),
  firstOrders7d: z.number().int(),
  byChannel7d: z.record(z.string(), z.number().int()),
  referralsPending: z.number().int(),
  referralsRewarded30d: z.number().int(),
  referralsBlocked30d: z.number().int(),
  passesWaiting: z.number().int(),
  passesActive: z.number().int(),
  qrScans7d: z.number().int(),
  cashback30dMinor: z.record(z.string(), z.number().int()),
  winback30d: z.number().int(),
});
export type GrowthSummary = z.infer<typeof GrowthSummarySchema>;

export const ReferralRowSchema = z.object({
  id: Id,
  referrer: z.object({ identityId: Id, name: z.string(), phoneMasked: z.string() }),
  referee: z.object({ identityId: Id, name: z.string(), phoneMasked: z.string() }),
  code: z.string(),
  status: ReferralStatusSchema,
  block: ReferralBlockSchema.nullable(),
  firstOrderNo: z.number().int().nullable(),
  rewardMinor: z.number().int(),
  country: CountryCodeSchema,
  createdAt: z.string(),
  rewardedAt: z.string().nullable(),
});
export type ReferralRow = z.infer<typeof ReferralRowSchema>;

export const ReferralQuerySchema = z.object({
  status: ReferralStatusSchema.optional(),
  search: z.string().trim().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ReferralQuery = z.infer<typeof ReferralQuerySchema>;

export const ReferralListSchema = z.object({
  rows: z.array(ReferralRowSchema),
  counts: z.record(ReferralStatusSchema, z.number().int()),
});
export type ReferralList = z.infer<typeof ReferralListSchema>;

/** GeoJSON polygon ring(s): [[[lng, lat], …]] — first ring is the outline, closed or not. */
export const PolygonCoordinatesSchema = z
  .array(z.array(z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)])).min(3))
  .min(1)
  .max(5);
export type PolygonCoordinates = z.infer<typeof PolygonCoordinatesSchema>;

export const CampusRowSchema = z.object({
  id: Id,
  name: z.string(),
  country: CountryCodeSchema,
  townId: Id.nullable(),
  townName: z.string().nullable(),
  polygon: PolygonCoordinatesSchema,
  center: z.object({ lat: z.number(), lng: z.number() }),
  /** Override of growth.campus_free_km for this campus (null = the setting). */
  freeKm: z.number().nullable(),
  emailDomains: z.array(z.string()),
  status: GrowthStatusSchema,
  ambassadors: z.number().int(),
  activePasses: z.number().int(),
  waitingPasses: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type CampusRow = z.infer<typeof CampusRowSchema>;

export const CampusListSchema = z.object({ rows: z.array(CampusRowSchema) });
export type CampusList = z.infer<typeof CampusListSchema>;

const DomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, 'Enter a domain like iitk.ac.in');

export const CampusInputSchema = z.object({
  name: z.string().trim().min(3).max(80),
  country: CountryCodeSchema,
  townId: Id.nullable().default(null),
  polygon: PolygonCoordinatesSchema,
  freeKm: z.number().min(0).max(10).nullable().default(null),
  emailDomains: z.array(DomainSchema).max(10).default([]),
  status: GrowthStatusSchema.default('active'),
});
export type CampusInput = z.infer<typeof CampusInputSchema>;

export const CampusUpdateInputSchema = CampusInputSchema.extend({
  expectedVersion: z.number().int().min(1),
});
export type CampusUpdateInput = z.infer<typeof CampusUpdateInputSchema>;

export const AmbassadorRowSchema = z.object({
  id: Id,
  name: z.string(),
  phoneMasked: z.string(),
  campusId: Id,
  campusName: z.string(),
  code: z.string(),
  status: GrowthStatusSchema,
  signups: z.number().int(),
  verifiedPasses: z.number().int(),
  firstOrders: z.number().int(),
  earnedMinor: z.number().int(),
  unpaidMinor: z.number().int(),
  country: CountryCodeSchema,
  version: z.number().int(),
  createdAt: z.string(),
});
export type AmbassadorRow = z.infer<typeof AmbassadorRowSchema>;

export const AmbassadorListSchema = z.object({ rows: z.array(AmbassadorRowSchema) });
export type AmbassadorList = z.infer<typeof AmbassadorListSchema>;

export const AmbassadorInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: z
    .string()
    .trim()
    .regex(/^\+(91[6-9]\d{9}|9779[678]\d{8})$/, 'Enter a valid Indian or Nepali mobile number'),
  campusId: Id,
  /** Empty = generated from the name. */
  code: GrowthCodeSchema.optional(),
});
export type AmbassadorInput = z.infer<typeof AmbassadorInputSchema>;

export const AmbassadorStatusInputSchema = z.object({
  status: GrowthStatusSchema,
  expectedVersion: z.number().int().min(1),
});
export type AmbassadorStatusInput = z.infer<typeof AmbassadorStatusInputSchema>;

export const AmbassadorPayoutInputSchema = z.object({
  /** Marks every unpaid earning of this ambassador paid (paid outside Capsico, e.g. UPI). */
  reference: z.string().trim().min(3).max(80),
});
export type AmbassadorPayoutInput = z.infer<typeof AmbassadorPayoutInputSchema>;

export const CampusPassRowSchema = z.object({
  id: Id,
  identityId: Id,
  name: z.string(),
  phoneMasked: z.string(),
  campusId: Id,
  campusName: z.string(),
  method: CampusPassMethodSchema,
  email: z.string().nullable(),
  status: CampusPassStatusSchema,
  ambassadorName: z.string().nullable(),
  /** Short-lived link to the ID photo (photo method only). */
  photoUrl: z.string().nullable(),
  validUntil: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewNote: z.string().nullable(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type CampusPassRow = z.infer<typeof CampusPassRowSchema>;

export const CampusPassQuerySchema = z.object({
  status: CampusPassStatusSchema.optional(),
  campusId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type CampusPassQuery = z.infer<typeof CampusPassQuerySchema>;

export const CampusPassListSchema = z.object({
  rows: z.array(CampusPassRowSchema),
  counts: z.record(CampusPassStatusSchema, z.number().int()),
});
export type CampusPassList = z.infer<typeof CampusPassListSchema>;

export const CampusPassDecisionInputSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
  expectedVersion: z.number().int().min(1),
});
export type CampusPassDecisionInput = z.infer<typeof CampusPassDecisionInputSchema>;

export const CreatorRowSchema = z.object({
  id: Id,
  name: z.string(),
  handle: z.string(),
  platform: CreatorPlatformSchema,
  code: z.string(),
  country: CountryCodeSchema,
  offerId: Id,
  offerTitle: z.string(),
  /** Paid to the creator per first order (manual payout). */
  feePerOrderMinor: z.number().int(),
  status: GrowthStatusSchema,
  stats: z.object({
    installs: z.number().int(),
    firstOrders: z.number().int(),
    orders: z.number().int(),
    discountMinor: z.number().int(),
    feesMinor: z.number().int(),
    costMinor: z.number().int(),
  }),
  version: z.number().int(),
  createdAt: z.string(),
});
export type CreatorRow = z.infer<typeof CreatorRowSchema>;

export const CreatorListSchema = z.object({ rows: z.array(CreatorRowSchema) });
export type CreatorList = z.infer<typeof CreatorListSchema>;

export const CreatorInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  handle: z.string().trim().min(2).max(60),
  platform: CreatorPlatformSchema,
  code: GrowthCodeSchema,
  country: CountryCodeSchema,
  /** The W10 offer under the hood (Capsico-funded, granted to people who use the code). */
  discountType: z.enum(['flat', 'percent']),
  flatMinor: z.number().int().min(100).nullable().default(null),
  percent: z.number().int().min(1).max(60).nullable().default(null),
  maxDiscountMinor: z.number().int().min(100).nullable().default(null),
  minOrderMinor: z.number().int().min(0).default(0),
  feePerOrderMinor: z.number().int().min(0).max(100_000).default(0),
  endsAt: isoInstant.nullable().default(null),
});
export type CreatorInput = z.infer<typeof CreatorInputSchema>;

export const CreatorStatusInputSchema = z.object({
  status: GrowthStatusSchema,
  expectedVersion: z.number().int().min(1),
});
export type CreatorStatusInput = z.infer<typeof CreatorStatusInputSchema>;

export const QrPosterRowSchema = z.object({
  id: Id,
  outletId: Id,
  outletName: z.string(),
  townName: z.string().nullable(),
  slug: z.string(),
  url: z.string(),
  offerCode: z.string(),
  status: GrowthStatusSchema,
  scans7d: z.number().int(),
  scans30d: z.number().int(),
  signups30d: z.number().int(),
  firstOrders30d: z.number().int(),
  createdBy: z.string(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type QrPosterRow = z.infer<typeof QrPosterRowSchema>;

export const QrPosterListSchema = z.object({ rows: z.array(QrPosterRowSchema) });
export type QrPosterList = z.infer<typeof QrPosterListSchema>;

export const QrPosterInputSchema = z.object({
  outletId: Id,
  offerCode: GrowthCodeSchema.default('WELCOME70'),
});
export type QrPosterInput = z.infer<typeof QrPosterInputSchema>;

export const QrPosterStatusInputSchema = z.object({
  status: GrowthStatusSchema,
  expectedVersion: z.number().int().min(1),
});
export type QrPosterStatusInput = z.infer<typeof QrPosterStatusInputSchema>;

export const QrPosterStatsSchema = z.object({
  posterId: Id,
  days: z.array(z.object({ day: z.string(), scans: z.number().int(), signups: z.number().int() })),
  totals: z.object({
    scans: z.number().int(),
    signups: z.number().int(),
    firstOrders: z.number().int(),
    conversionPercent: z.number(),
  }),
});
export type QrPosterStats = z.infer<typeof QrPosterStatsSchema>;

export const AttributionQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
  country: CountryCodeSchema.optional(),
});
export type AttributionQuery = z.infer<typeof AttributionQuerySchema>;

export const AttributionReportRowSchema = z.object({
  channel: AttributionChannelSchema,
  signups: z.number().int(),
  firstOrders: z.number().int(),
  orders: z.number().int(),
  gmvMinor: z.number().int(),
  discountMinor: z.number().int(),
  creditMinor: z.number().int(),
  conversionPercent: z.number(),
});
export type AttributionReportRow = z.infer<typeof AttributionReportRowSchema>;

export const AttributionReportSchema = z.object({
  days: z.number().int(),
  country: CountryCodeSchema.nullable(),
  rows: z.array(AttributionReportRowSchema),
  topCodes: z.array(
    z.object({
      code: z.string(),
      channel: AttributionChannelSchema,
      signups: z.number().int(),
      firstOrders: z.number().int(),
    }),
  ),
});
export type AttributionReport = z.infer<typeof AttributionReportSchema>;

export const CashbackRowSchema = z.object({
  orderId: Id,
  orderNo: z.number().int().nullable(),
  customer: z.string(),
  country: CountryCodeSchema,
  orderTotalMinor: z.number().int(),
  amountMinor: z.number().int(),
  status: z.enum(['credited', 'skipped']),
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type CashbackRow = z.infer<typeof CashbackRowSchema>;

export const CashbackListSchema = z.object({
  rows: z.array(CashbackRowSchema),
  totals: z.record(z.string(), z.number().int()),
});
export type CashbackList = z.infer<typeof CashbackListSchema>;

export const WinbackRowSchema = z.object({
  id: Id,
  customer: z.string(),
  country: CountryCodeSchema,
  creditMinor: z.number().int(),
  holdout: z.boolean(),
  offerCode: z.string().nullable(),
  convertedOrderNo: z.number().int().nullable(),
  grantedAt: z.string(),
});
export type WinbackRow = z.infer<typeof WinbackRowSchema>;

export const WinbackListSchema = z.object({
  rows: z.array(WinbackRowSchema),
  converted30d: z.number().int(),
  granted30d: z.number().int(),
});
export type WinbackList = z.infer<typeof WinbackListSchema>;

/** Config keys the Cashback + win-back settings screen edits (through the settings registry). */
export const GROWTH_SETTING_KEYS = {
  cashbackPercent: 'growth.cashback_percent',
  cashbackMinOrder: 'growth.cashback_min_order',
  cashbackCap: 'growth.cashback_cap',
  promoCreditDays: 'growth.promo_credit_expiry_days',
  winbackAfterDays: 'growth.winback_after_days',
  winbackCredit: 'growth.winback_credit',
  winbackCreditDays: 'growth.winback_credit_days',
  winbackOnceDays: 'growth.winback_once_days',
  referralReward: 'growth.referral_reward',
  referralRewardDays: 'growth.referral_reward_days',
  referralMaxPerMonth: 'growth.referral_max_per_month',
  attributionDays: 'growth.attribution_days',
  campusFreeKm: 'growth.campus_free_km',
  campusPassDays: 'growth.campus_pass_days',
  ambassadorReward: 'growth.ambassador_reward',
} as const;

/** Offer codes the growth engine seeds (W10 offers with audience "granted"). */
export const GROWTH_OFFER_CODES = {
  referral: 'FRIEND75',
  winback: 'COMEBACK30',
  qrDefault: 'WELCOME70',
} as const;

/** Job summaries (job:notify-dispatch / job:notify-journeys). */
export const NotifyDispatchResultSchema = z.object({
  events: z.number().int(),
  sent: z.number().int(),
  blocked: z.number().int(),
  deferred: z.number().int(),
  failed: z.number().int(),
});
export type NotifyDispatchResult = z.infer<typeof NotifyDispatchResultSchema>;

export const NotifyJourneysResultSchema = z.object({
  cartAbandoned: z.number().int(),
  firstOrderNudge: z.number().int(),
  winBack: z.number().int(),
  rateOrder: z.number().int(),
  holdout: z.number().int(),
  passesExpired: z.number().int(),
  referralsExpired: z.number().int(),
});
export type NotifyJourneysResult = z.infer<typeof NotifyJourneysResultSchema>;
